import { ValueOf } from 'type-fest';
import { INTEGRATION_SERVICE_NAMES } from '@constants/integration';
import { TodoListField } from 'hooks/useSimpleTodoList';
import { FetchCsvImportStatusSuccessResponse } from 'IntegrationModule/types';

export type ActivePendingTypes = {
  active: number;
  createOnMosaic: number;
  createOnTarget: number;
  updateOnMosaic: number;
  updateOnTarget: number;
  totalPending: number;
  pendingToMosaic: number;
  pendingToTarget: number;
};

export type ServiceMetricsActivePendingTotalCountsTypes = {
  [key: string]: ActivePendingTypes;
};

export type PerIntervalType = {
  offset: number;
  data: Array<any>; // Get the correct any
  hasReachTheEnd?: boolean;
};

export type DataHistoryPerDataTypesPerIntervalType = {
  day: PerIntervalType;
  week: PerIntervalType;
  month: PerIntervalType;
  year: PerIntervalType;
};

export type TelemetryLastUpdatesType = {
  target_service_id: number;
  data_type: string;
  updated_at: string;
};

/**
 * Represents data overview per data type based on targetServiceId
 */
export type IntegrationDataOverviewPerDataTypesType = {
  mosaicTeamId: number;
  targetServiceId: number;
  dataType: string;
  countofdatatype: string;
  mostrecentupdate: string;
  updatedinlasthour?: string;
  updatedinlastday?: string;
  updatedinlastweek?: string;
};

export type RecentErrorsBodyType = {
  targetService: string; //"quickbooks-desktop",
  targetServiceId: number | string;
  limit: number;
  offset: number;
  agentOnly: boolean;
  startDate: string; // "09/01/2021"
};

export type RecentErrorsType = {
  [key: string]: any;
  id: number;
  action: string;
  origin: string;
  target: string;
  mosaic_team_id: number;
  mosaic_team_name?: string;
  target_service_id: number;
  session_id: string;
  payload: string;
  data_type: string;
  response: string;
  response_time: number;
  created_at: string;
  updated_at: string;
  details: string;
  total_rows: number;
  message: string;
};

export type NetworkErrorsLogType = {
  createdAt: string;
  dataType?: string;
  updatedAt: string;
  parsedMetaData: Record<any, any>;
  parsedError: any[];
  apiRequestId: string;
  entityId: string;
  error: string; //'[{"2886996": "{:phase_id=>[\\"phase does not belong to project\\"]}"}]';
  id: number;
  metadata: string; //'{"id": "7846734", "sessionId": "113217148", "mosaicTeamId": "4045", "requestedByIS": "true", "targetServiceId": "6"}';
  origin: string; //"https://prod-api-server.mosaicapp.com";
  requestedByIS: string; //"true";
  sessionId: string; //"113217148";
  targetServiceId: number;
  mosaicTeamId: string;
};

export type RecentErrorStateType = {
  data: Array<RecentErrorsType>;
  total: number;
  bodyOptions: RecentErrorsBodyType;
};

export type TelemetryLastUpdatesBodyType = {
  targetService: string;
  targetServiceIds: Array<number>;
};

export type QueueStatusType = [number, Record<string, number>];

export type ExceedRetriesInstance = {
  count: string;
  dataType: string;
  mosaicTeamId: number;
  targetServiceId: number;
};

export type ExceededRetriesType = Record<number, Array<ExceedRetriesInstance>>;

// Need to fix any
export type PendingEntitiesInstance = {
  id: number;
  dataType?: string;
  mappingStatus?: string;
  mosaicTeamId?: number;
  mosaicId?: number;
  targetService?: string;
  targetId?: string;
  isArchived?: boolean;
  archivedAt?: string;
  mosaicData: {
    date?: string;
    rate?: string;
    hours?: string;
    title?: string;
    status?: string;
    boardId?: string;
    phaseId?: string;
    mosaicId?: string;
    accountId?: string;
    boardName?: string;
    phaseName?: string;
    projectId?: string;
    activityId?: string;
    isBillable?: false;
    accountName?: string;
    accountEmail?: string;
    mosaicTeamId?: string;
    projectTitle?: string;
    activityTitle?: string;
    descriptionId?: string;
  };
  targetData: {
    date?: string;
    hours?: number;
    phaseId?: any; // unsure type
    billable?: boolean;
    mosaicId?: any; // unsure type
    targetId?: string;
    accountId?: any; // unsure type
    projectId?: any; // unsure type
    activityId?: any; // unsure type
    description?: string;
    mosaicTeamId?: any; // unsure type
    targetPhaseId?: any; // unsure type
    targetCreatedAt?: string;
    targetProjectId?: string;
    targetServiceId?: number;
    targetUpdatedAt?: string;
    isTargetArchived?: boolean;
    targetActivityId?: string;
    targetEmployeeId?: string;
    targetHourlyRate?: number;
  };
  uuid?: string;
  createdAt?: string;
  UpdatedAt?: string;
  integrationId?: number;
  targetServiceId: number;
  _action?: any;
  _requeue?: boolean;
  mosaicDataType?: string;
  settings?: any;
  retryCount?: number;
  mosaicDataHash?: string;
  targetDataHash?: string;
  createSource?: string | null;
  updateSource?: string | null;
  waitingResponse?: boolean;
};

export type ActiveIntegrations = {
  targetService: string;
  count: string;
};

export type NetworkLogDataType = {
  action: string;
  createdAt: string;
  updatedAt?: string;
  dataType: string;
  id: number;
  isArray: boolean;
  mosaicTeamId: number;
  origin: string;
  payload: {
    actionType: string;
    billable: boolean;
    dataPayload: Record<any, any>;
    dataType: string;
    isBillable: boolean;
    metadata: Record<any, any>;
    mosaicTeamId: number;
    targetService: string;
    targetServiceId: number;
    targetSessionId: number;
  };
  response: string;
  sessionId: number;
  target: string;
  targetServiceId: number;
};

export type ApiRequestLogDataType = {
  action_log_type: string;
  api_request_id: string;
  created_at: string;
  destination: string;
  id?: string;
  payload: {
    apiRequestId: string;
    dataPayload?: any;
    errors: Record<any, any>[];
    metadata: Record<any, any>;
  };
  code: number;
  metadata: Record<any, any>;
  response: Record<any, any>;
  updated_at: string;
};

export type IntegrationNote = TodoListField;

export enum IntegrationState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  ARCHIVED = 'ARCHIVED',
}

export type LastEditedBy = {
  accountMosaicId: number | null;
  timestamp: string;
  galaxyUserId?: string | null;
  source?: 'galaxy' | 'web';
  stateChange?: {
    previousState: IntegrationState | null;
    newState: IntegrationState;
    reason?: string;
  };
};

export type Integration = {
  id: number;
  mosaicTeamId: number;
  archivedAt?: string;
  state?: IntegrationState;
  targetService: string;
  targetServiceId: number;
  isSourceMosaic?: boolean;
  createdAt?: string;
  updatedAt?: string;
  deletionInProgress?: boolean;
  lastEditedBy?: LastEditedBy | null;
  agentDomain?: {
    settings?: {
      useV2ModalAfter?: string;
    };
  };
  notes: IntegrationNote[] | null;
  settings?: {
    label?: string;
    configureWithV2Modal?: boolean;
    addingHash?: boolean;
    sendToAgent?: {
      time_entry?: boolean;
    };
    sendToMosaic?: {
      time_entry?: boolean;
    };
    /** Number of times per day the integration fetches data (GLX-1609). */
    customDailyFetchCount?: number;
    /**
     * Scheduled UTC "HH:mm" sync anchor times (GLX-1656). The IS stores either
     * a single string (legacy) or an array, and only honors the first 4.
     */
    customDailyFetchTime?: string | string[];
    syncSetting?: {
      phase?: {
        name?: string;
        endDate?: string;
        startDate?: string;
        isBillable?: string;
      };
      project?: {
        name?: string;
        isBillable?: string;
      };
    };
    autoLinkPhase?: boolean;
    doNotAutoSync?: boolean;
    defaultBoardId?: number;
    autoLinkProject?: boolean;
    targetServiceId?: number;
    autoLinkAllPhase?: boolean;
    doAutoSyncTimeEntry?: boolean;
    humanFriendlySetting?: {
      agentSettings?: Record<any, unknown>;
      billingStatus?: {
        importMemberRate?: {
          billRates?: boolean;
          costRates?: boolean;
        };
      };
      informationToLink?: {
        phase?: boolean[];
      };
      phaseDatesToMosaic?: string;
      editingImportedData?: {
        phaseDateToMosaic?: string;
        phaseTitlesToMosaic?: string;
      };
      phaseTitlesToMosaic?: string;
    };
    autoLinkPhaseExcludeList?: Record<any, unknown>;
  };
};

export type TargetService = ValueOf<typeof INTEGRATION_SERVICE_NAMES>;

export type IntegrationHealth = {
  id: number;
  date: string;
  serviceName: ValueOf<typeof INTEGRATION_SERVICE_NAMES>;
  requestCount: number;
  successCount: number;
  errors: {
    AuthorizationError?: number;
    ConnectionError?: number;
    DataError?: number;
    InvalidParamsError?: number;
    UnknownError?: number;
  };
  targetServiceId?: number;
};

export type CsvUploadInfo = {
  count: number | null;
  countInDB: string;
  createdAt: string;
  dataType: string;
  id: number;
  isFinished: boolean;
  mosaicTeamId: number;
  sessionId: string;
  updatedAt: string;
};

export type IntegrationAgentDomains = {
  service: string;
  label: string;
  url: string;
};
